import React from 'react'
import {
    Card,
    FormControl,
    FormControlLabelProps,
    Grid,
    IconButton,
    PropTypes,
    Typography,
} from '@material-ui/core'
import { Field } from 'react-final-form'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import clsx from 'clsx'
import Checkbox from '@material-ui/core/Checkbox'
import { Trans, useTranslation } from 'react-i18next'
import { BusinessHour } from '@sugg-gestion/ubidreams-react-suggpro/src/resources/establishment/types'
import { DayOfWeek } from '@sugg-gestion/ubidreams-react-suggpro'
import { KeyboardTimePicker } from '@material-ui/pickers'
import classNames from 'classnames'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import FormHelperText from '@material-ui/core/FormHelperText'
import Button from '@material-ui/core/Button'
import AddIcon from '@material-ui/icons/Add'
import moment, { Moment } from 'moment/moment'
import { GridSize } from '@material-ui/core/Grid/Grid'
import { ClassNameMap } from '@material-ui/styles/withStyles/withStyles'

interface Props {
    name: string
    margin?: PropTypes.Margin
    day: DayOfWeek
    values: Record<string, any>
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            width: '100%',
            borderColor: theme.palette.info.light,
            borderWidth: 1,
            borderLeftWidth: 4,
            borderStyle: 'solid',
        },
        info: {
            color: theme.palette.primary.main,
        },
        error: {
            color: theme.palette.error.main,
        },
        adornmentError: {
            '& > button': {
                color: theme.palette.error.main,
            },
        },
        details: {
            marginLeft: theme.spacing(4),
            marginTop: theme.spacing(-1.5),
        },
        checkbox: {
            'width': 130,
            'margin': 0,
            'backgroundColor': theme.palette.border.contrastText,
            'borderRadius': 6,
            'borderWidth': 1,
            'borderColor': theme.palette.border.main,
            '& .MuiFormControlLabel-label': {
                width: '100%',
                paddingLeft: theme.spacing(2),
                fontWeight: theme.fontsWeight.semiBold,
            },
        },
        checkboxServices: {
            'width': '100%',
            'backgroundColor': theme.palette.background.default,
            '& .MuiButtonBase-root .MuiIconButton-label': {
                color: theme.palette.border.dark,
            },
        },
        checkboxSelected: {
            '& .MuiIconButton-label': {
                color: 'transparent',
                backgroundColor: theme.palette.background.default,
            },
        },
        checkboxSelectedServices: {
            '& .MuiIconButton-label': {
                color: theme.palette.action.active,
            },
        },

        color: {
            'fontWeight': theme.fontsWeight.semiBold,

            '& .MuiCheckbox-colorSecondary': {
                Color: 'transparent',
            },
            '& .MuiCheckbox-root': {
                color: 'transparent',
            },

            '& .MuiCheckbox-colorSecondary.Mui-checked': {
                color: '#F38355',
            },
        },

        colorServices: {
            'fontWeight': theme.fontsWeight.semiBold,

            '&.MuiCheckbox-colorSecondary.Mui-checked': {
                color: theme.palette.info.main,
            },
        },

        day: {
            marginTop: theme.spacing(1),
            fontWeight: theme.fontsWeight.semiBold,
        },
        space: {
            marginTop: theme.spacing(1),
        },
        left: {
            marginLeft: theme.spacing(-4),
        },

        leftbuton: {
            marginLeft: theme.spacing(-2),
        },

        hourDetails: {
            paddingLeft: theme.spacing(2),
        },
        timeAssert: {
            marginTop: theme.spacing(3),
            textAlign: 'right',
            paddingRight: theme.spacing(),
        },
        additionalTime: {
            textAlign: 'center',
            paddingTop: theme.spacing(),
        },
        additionalTimeServices: {
            marginBottom: theme.spacing(2),
            color: theme.palette.info.main,
        },
        space3: {
            [theme.breakpoints.up('md')]: {
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 350,
                marginLeft: theme.spacing(1),
            },
            [theme.breakpoints.down('sm')]: {
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 310,
                marginLeft: theme.spacing(1),
            },
            [theme.breakpoints.down('xs')]: {
                textAlign: 'center',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: 242,
            },
        },

        ecart: {
            [theme.breakpoints.down('sm')]: { marginLeft: theme.spacing(1) },

            [theme.breakpoints.down('xs')]: {
                marginLeft: theme.spacing(1),
            },
        },

        deleteContainer: {
            width: 30,
            [theme.breakpoints.up('md')]: {
                marginLeft: theme.spacing(-5),
            },
            [theme.breakpoints.down('xs')]: {
                marginLeft: theme.spacing(-20),
            },
        },
    }),
)

const getTime = (value: string) => {
    const startTimeDetail = value.split(':')
    const now = moment()
    return now.hour(parseInt(startTimeDetail[0])).minute(parseInt(startTimeDetail[1]))
}

type Hour = {
    startTime: string
    endTime: string
}

interface HoursGridProps<T extends Hour> {
    value: Array<T>
    isActive: boolean
    onActivate: () => void
    onDeactivate: () => void
    activateLabel: string
    deactivateLabel: string
    labelPlacement: FormControlLabelProps['labelPlacement']
    onChangeTime: (index: number, time: any, field: string) => void
    onDeleteTime: (index: number) => void
    errors: Array<string> | undefined
    onAddHours: () => void
    children?: (index: number) => React.ReactNode
    checkboxGridSize?: GridSize
    hoursGridSize?: GridSize
    classes?: Partial<ClassNameMap>
    addActionLabel?: string | React.ReactNode
    additionalTimeButtonColor?: PropTypes.Color
}

const HoursGrid = <T extends Hour>({
    value,
    isActive,
    onActivate,
    onDeactivate,
    activateLabel,
    deactivateLabel,
    labelPlacement,
    onChangeTime,
    onDeleteTime,
    onAddHours,
    errors,
    children,
    checkboxGridSize = 2,
    hoursGridSize = 10,
    classes: classesProp,
    addActionLabel = <Trans i18nKey="actions.addHours" />,
    additionalTimeButtonColor = 'primary',
}: HoursGridProps<T>) => {
    const classes = useStyles()
    const { t } = useTranslation()
    return (
        <Grid container>
            <Grid item md={checkboxGridSize} xs={12}>
                <FormControlLabel
                    className={clsx(classes.checkbox, classesProp?.checkbox, {
                        [classes.checkboxSelected]: isActive,
                        [classesProp?.checkboxSelected as string]: isActive,
                    })}
                    control={
                        <Checkbox
                            checked={!isActive}
                            className={clsx(classes.color, classesProp?.color)}
                            onChange={() => {
                                if (isActive) {
                                    onDeactivate()
                                } else {
                                    onActivate()
                                }
                            }}
                        />
                    }
                    label={isActive ? activateLabel : deactivateLabel}
                    labelPlacement={labelPlacement}
                />
            </Grid>
            <Grid item md={hoursGridSize} xs={12}>
                <Grid container className={classes.hourDetails} alignItems="center">
                    {value.map((hourFromDay, index) => {
                        const error =
                            errors !== undefined && errors[index] !== undefined ? errors[index] : undefined
                        return (
                            <React.Fragment key={index}>
                                <Grid item xs={5}>
                                    <Grid container spacing={1}>
                                        <Grid item xs={3}>
                                            <Typography className={classes.timeAssert}>
                                                <Trans i18nKey="form.startTime" />
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={9}>
                                            <KeyboardTimePicker
                                                error={error !== undefined}
                                                ampm={false}
                                                label=""
                                                value={getTime(hourFromDay.startTime)}
                                                fullWidth
                                                margin="normal"
                                                onChange={(value) => onChangeTime(index, value, 'startTime')}
                                                InputAdornmentProps={{
                                                    className: classNames({
                                                        [classes.adornmentError]: error !== undefined,
                                                    }),
                                                }}
                                                inputProps={{
                                                    className: classNames({
                                                        [classes.error]: error !== undefined,
                                                    }),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={5}>
                                    <Grid container key={index} spacing={0}>
                                        <Grid item xs={3} className={classes.timeAssert}>
                                            {t('form.endTime')}
                                        </Grid>
                                        <Grid item xs={9}>
                                            <KeyboardTimePicker
                                                error={error !== undefined}
                                                ampm={false}
                                                label=""
                                                value={getTime(hourFromDay.endTime)}
                                                fullWidth
                                                margin="normal"
                                                onChange={(value) => onChangeTime(index, value, 'endTime')}
                                                InputAdornmentProps={{
                                                    className: classNames({
                                                        [classes.adornmentError]: error !== undefined,
                                                    }),
                                                }}
                                                inputProps={{
                                                    className: classNames({
                                                        [classes.error]: error !== undefined,
                                                    }),
                                                }}
                                                cancelLabel={t('actions.cancel')}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={2}>
                                    {value.length > 1 && (
                                        <IconButton onClick={() => onDeleteTime(index)}>
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    {error !== undefined && (
                                        <div>
                                            <FormHelperText className={classes.error}>{error}</FormHelperText>
                                        </div>
                                    )}
                                </Grid>
                                {children && children(index)}
                            </React.Fragment>
                        )
                    })}

                    {value.length > 0 && (
                        <Grid
                            item
                            xs={12}
                            className={clsx(classes.additionalTime, classesProp?.additionalTime)}
                        >
                            <Button
                                variant="outlined"
                                color={additionalTimeButtonColor}
                                onClick={onAddHours}
                                startIcon={<AddIcon />}
                            >
                                {addActionLabel}
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Grid>
    )
}

const BusinessHoursField: React.FC<Props> = ({ name, margin = 'normal', day, values }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const getDayString = (day: DayOfWeek) => {
        switch (day) {
            case DayOfWeek.Monday:
                return 'monday'
            case DayOfWeek.Tuesday:
                return 'tuesday'
            case DayOfWeek.Wednesday:
                return 'wednesday'
            case DayOfWeek.Thursday:
                return 'thursday'
            case DayOfWeek.Friday:
                return 'friday'
            case DayOfWeek.Saturday:
                return 'saturday'
            case DayOfWeek.Sunday:
                return 'sunday'
        }
    }

    return (
        <Field<Array<BusinessHour>> name={name}>
            {({ input: { onChange, value }, meta }) => {
                return (
                    <FormControl component="fieldset" fullWidth margin={margin}>
                        <Grid container spacing={2}>
                            <Grid item md={2} xs={12}>
                                <Typography className={classes.day}>
                                    <Trans i18nKey={'form.' + getDayString(day)} />
                                </Typography>
                            </Grid>
                            <Grid item md={10} xs={12}>
                                <HoursGrid
                                    value={value}
                                    isActive={value.length > 0}
                                    onActivate={function (): void {
                                        const dayBefore = (((day % 7) + 6) % 7) as DayOfWeek
                                        if (values['regularHours']['key' + dayBefore].length > 0) {
                                            value = []
                                            values['regularHours']['key' + dayBefore].forEach(
                                                (previousHours: any) => {
                                                    value.push(previousHours)
                                                },
                                            )
                                        } else {
                                            value = [
                                                {
                                                    startTime: '12:00',
                                                    endTime: '14:00',
                                                    services: [],
                                                },
                                            ]
                                        }
                                        onChange([...value])
                                    }}
                                    onAddHours={function (): void {
                                        value = [
                                            ...value,
                                            {
                                                startTime: '12:00',
                                                endTime: '14:00',
                                                services: [],
                                            },
                                        ]
                                        onChange([...value])
                                    }}
                                    onDeactivate={function (): void {
                                        value = []
                                        onChange([...value])
                                    }}
                                    activateLabel={t('form.hoursClose')}
                                    deactivateLabel={t('form.hoursClose')}
                                    labelPlacement={'start'}
                                    onChangeTime={(index, time, field) => {
                                        const moment = time as Moment
                                        if (moment !== null && moment.isValid()) {
                                            let hourFromDay = value[index]
                                            hourFromDay = {
                                                ...hourFromDay,
                                                [field]: moment.format('HH:mm'),
                                            }
                                            value[index] = hourFromDay
                                            onChange([...value])
                                        }
                                    }}
                                    onDeleteTime={(index) => {
                                        value.splice(index, 1)
                                        onChange([...value])
                                    }}
                                    errors={meta.error?.map((error: any) => error?.message)}
                                >
                                    {(index) => {
                                        const services = value[index]?.services ?? []
                                        return (
                                            <Card className={clsx(classes.container)} elevation={0}>
                                                <HoursGrid
                                                    value={services}
                                                    isActive={services.length > 0}
                                                    onActivate={function (): void {
                                                        value[index].services = [
                                                            {
                                                                startTime: value[index].startTime,
                                                                endTime: value[index].endTime,
                                                            },
                                                        ]
                                                        onChange([...value])
                                                    }}
                                                    onAddHours={function (): void {
                                                        value[index].services = [
                                                            ...value[index].services,
                                                            {
                                                                startTime: value[index].startTime,
                                                                endTime: value[index].endTime,
                                                            },
                                                        ]
                                                        onChange([...value])
                                                    }}
                                                    onDeactivate={function (): void {
                                                        value[index].services = []
                                                        onChange([...value])
                                                    }}
                                                    activateLabel={t('form.services')}
                                                    deactivateLabel={t('form.serviceAll')}
                                                    labelPlacement={'end'}
                                                    onChangeTime={(indexService, time, field) => {
                                                        const moment = time as Moment
                                                        if (moment !== null && moment.isValid()) {
                                                            let hourFromDay =
                                                                value[index].services[indexService]
                                                            hourFromDay = {
                                                                ...hourFromDay,
                                                                [field]: moment.format('HH:mm'),
                                                            }
                                                            value[index].services[indexService] = hourFromDay
                                                            onChange([...value])
                                                        }
                                                    }}
                                                    onDeleteTime={(indexService) => {
                                                        value[index].services.splice(indexService, 1)
                                                        onChange([...value])
                                                    }}
                                                    errors={meta.error?.[index]?.services}
                                                    checkboxGridSize={12}
                                                    hoursGridSize={12}
                                                    addActionLabel={<Trans i18nKey="actions.addService" />}
                                                    classes={{
                                                        checkbox: classes.checkboxServices,
                                                        checkboxSelected: classes.checkboxSelectedServices,
                                                        color: classes.colorServices,
                                                        additionalTime: classes.additionalTimeServices,
                                                    }}
                                                    additionalTimeButtonColor="inherit"
                                                />
                                            </Card>
                                        )
                                    }}
                                </HoursGrid>
                            </Grid>
                        </Grid>
                    </FormControl>
                )
            }}
        </Field>
    )
}

export default BusinessHoursField
